import { Box, CircularProgress, Modal, Stack, Tooltip, Typography } from "@mui/material";
import MDBadge from "components/MDBadge";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "40%",
  maxHeight: "80%",
  bgcolor: "background.paper",
  borderRadius: "10px",
  overflow: "auto",
  boxShadow: 24,
  p: 4,
};

const conditionApplyContent = {
  all_product: "All products",
  specific_product: "Specific products",
  specific_collection: "Specific collections",
  product_tag: "Product tag",
  product_title: "Product title",
  exclude_product: "Exclude products",
  exclude_collection: "Exclude collections",
  exclude_tag: "Exclude tag",
  exclude_title: "Exclude title",
};

const discountApplyContent = {
  percentage: "Percentage",
  fixed: "Fixed",
  new_price: "New price",
};

const tagLogicContent = {
  all: "All",
  at_least_one: "At least one",
};

const titleApplyContent = {
  product: "Product title",
  variant: "Variant title",
};

const titleLogicContent = {
  matches: "Matches",
  include: "Include",
};

function ModalDiscount({ active, onClose, dataIndex }) {
  const discounts = useSelector((state) => state.discount.list);
  const [loading, setLoading] = useState(true);
  const [discount, setDiscount] = useState({});

  useEffect(() => {
    setLoading(true);
    setDiscount(discounts?.[dataIndex] || {});
  }, [dataIndex, discounts]);

  useEffect(() => {
    setLoading(false);
  }, [discount]);

  const condition = useMemo(() => discount?.condition || {}, [discount]);

  const discountValue = useMemo(() => {
    if (!discount?.discount_value) return "—";
    if (discount?.discount_apply === "percentage") return `${discount.discount_value}%`;
    return discount.discount_value;
  }, [discount]);

  const combineWith = useMemo(
    () =>
      [
        discount?.combines_with_product_discounts && "Product discount",
        discount?.combines_with_order_discounts && "Order discount",
        discount?.combines_with_shipping_discounts && "Shipping discount",
      ].filter(Boolean),
    [discount]
  );

  const tags = useMemo(() => discount?.tags || [], [discount]);

  const combineWithTags = useMemo(() => discount?.combine_with_tags || [], [discount]);

  return (
    <Modal open={active} onClose={onClose}>
      <Box sx={style}>
        {loading ? (
          <Box>
            <CircularProgress color="info" />
          </Box>
        ) : (
          <>
            <Typography>{`Discount: ${discount?.title || ""}`}</Typography>
            <Box sx={{ marginTop: 2 }}>
              <Stack spacing={2}>
                {discount?.discount_type === "code" && (
                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Code:</Typography>
                    <Typography variant="body2" color="tomato">
                      {discount?.code || "Automatic discount (no code)"}
                    </Typography>
                  </Stack>
                )}

                <Stack direction="row" spacing={1}>
                  <Typography variant="body2">Discount apply:</Typography>
                  <MDBadge
                    badgeContent={
                      discountApplyContent[discount?.discount_apply] ||
                      discount?.discount_apply ||
                      "—"
                    }
                    color="success"
                    variant="gradient"
                    size="sm"
                  />
                  <Typography variant="body2" color="tomato">
                    {discountValue}
                  </Typography>
                </Stack>

                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Typography variant="body2">Condition apply:</Typography>
                    {Array.isArray(condition?.condition_apply) ? (
                      condition?.condition_apply?.map((item) => (
                        <MDBadge
                          key={item}
                          badgeContent={conditionApplyContent[item] || item}
                          color="success"
                          variant="gradient"
                          size="sm"
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="tomato">
                        {conditionApplyContent[condition?.condition_apply] ||
                          condition?.condition_apply ||
                          "—"}
                      </Typography>
                    )}
                  </Stack>

                  {condition?.specific_products?.length > 0 && (
                    <Stack>
                      <Typography variant="body2">Specific products:</Typography>
                      {condition?.specific_products?.map((item, index) => {
                        return (
                          <Stack direction="row" spacing={1} key={index}>
                            {item.variants?.length == 1 &&
                            item.variants[0].title == "Default Title" ? (
                              <Typography variant="body2" color="tomato">
                                {`- ${item.title}`}
                              </Typography>
                            ) : (
                              <Tooltip
                                title={
                                  <Stack spacing={0.5} alignItems="flex-start">
                                    {item.variants?.map((variant, vIndex) => (
                                      <Typography key={vIndex} variant="caption">
                                        • {variant.title}
                                      </Typography>
                                    ))}
                                  </Stack>
                                }
                                arrow
                                placement="right"
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      backgroundColor: "#FFFFFF",
                                      color: "#000000",
                                      boxShadow:
                                        "0 1px 3px rgba(0, 0, 0, .3), 0 4px 8px 3px rgba(0, 0, 0, .15)",
                                    },
                                  },
                                  arrow: {
                                    sx: {
                                      color: "#FFFFFF",
                                      filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.3))",
                                    },
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="tomato"
                                  sx={{
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  {`- ${item.title}`}
                                </Typography>
                              </Tooltip>
                            )}
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}

                  {condition?.specific_collections?.length > 0 && (
                    <Stack spacing={0.5}>
                      <Typography variant="body2">Specific collections:</Typography>
                      {condition.specific_collections?.map((item) => (
                        <Typography key={item.id} variant="body2" color="tomato">
                          {`- ${item.title}`}
                        </Typography>
                      ))}
                    </Stack>
                  )}

                  {condition?.product_tags?.length > 0 && (
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2">Product tags:</Typography>
                      <MDBadge
                        badgeContent={
                          tagLogicContent[condition.product_tag_logic] ||
                          condition.product_tag_logic
                        }
                        color="success"
                        variant="gradient"
                        size="sm"
                      />
                      <Typography variant="body2" color="tomato">
                        {condition.product_tags.join(", ")}
                      </Typography>
                    </Stack>
                  )}

                  {condition?.product_title && (
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2">Product title:</Typography>
                      <MDBadge
                        badgeContent={
                          titleApplyContent[condition.product_title_apply] ||
                          condition.product_title_apply
                        }
                        color="success"
                        variant="gradient"
                        size="sm"
                      />
                      <MDBadge
                        badgeContent={
                          titleLogicContent[condition.product_title_logic] ||
                          condition.product_title_logic
                        }
                        color="success"
                        variant="gradient"
                        size="sm"
                      />
                      <Typography variant="body2" color="tomato">
                        {condition.product_title}
                      </Typography>
                    </Stack>
                  )}

                  {condition?.exclude_products?.length > 0 && (
                    <Stack>
                      <Typography variant="body2">Exclude products:</Typography>
                      {condition?.exclude_products?.map((item, index) => {
                        return (
                          <Stack direction="row" spacing={1} key={index}>
                            {item.variants?.length == 1 &&
                            item.variants[0].title == "Default Title" ? (
                              <Typography variant="body2" color="tomato">
                                {`- ${item.title}`}
                              </Typography>
                            ) : (
                              <Tooltip
                                title={
                                  <Stack spacing={0.5} alignItems="flex-start">
                                    {item.variants?.map((variant, vIndex) => (
                                      <Typography key={vIndex} variant="caption">
                                        • {variant.title}
                                      </Typography>
                                    ))}
                                  </Stack>
                                }
                                arrow
                                placement="right"
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      backgroundColor: "#FFFFFF",
                                      color: "#000000",
                                      boxShadow:
                                        "0 1px 3px rgba(0, 0, 0, .3), 0 4px 8px 3px rgba(0, 0, 0, .15)",
                                    },
                                  },
                                  arrow: {
                                    sx: {
                                      color: "#FFFFFF",
                                      filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.3))",
                                    },
                                  },
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="tomato"
                                  sx={{
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                  }}
                                >
                                  {`- ${item.title}`}
                                </Typography>
                              </Tooltip>
                            )}
                          </Stack>
                        );
                      })}
                    </Stack>
                  )}

                  {condition?.exclude_collections?.length > 0 && (
                    <Stack spacing={0.5}>
                      <Typography variant="body2">Exclude collections:</Typography>
                      {condition.exclude_collections?.map((item) => (
                        <Typography key={item.id} variant="body2" color="tomato">
                          {`- ${item.title}`}
                        </Typography>
                      ))}
                    </Stack>
                  )}

                  {condition?.exclude_tags?.length > 0 && (
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2">Exclude tags:</Typography>
                      <MDBadge
                        badgeContent={
                          tagLogicContent[condition.exclude_tag_logic] ||
                          condition.exclude_tag_logic
                        }
                        color="success"
                        variant="gradient"
                        size="sm"
                      />
                      <Typography variant="body2" color="tomato">
                        {condition.exclude_tags.join(", ")}
                      </Typography>
                    </Stack>
                  )}

                  {condition?.exclude_title && (
                    <Stack direction="row" spacing={1}>
                      <Typography variant="body2">Exclude title:</Typography>
                      <MDBadge
                        badgeContent={
                          titleApplyContent[condition.exclude_title_apply] ||
                          condition.exclude_title_apply
                        }
                        color="success"
                        variant="gradient"
                        size="sm"
                      />
                      <MDBadge
                        badgeContent={
                          titleLogicContent[condition.exclude_title_logic] ||
                          condition.exclude_title_logic
                        }
                        color="success"
                        variant="gradient"
                        size="sm"
                      />
                      <Typography variant="body2" color="tomato">
                        {condition.exclude_title}
                      </Typography>
                    </Stack>
                  )}

                  <Stack direction="row" spacing={1}>
                    <Typography variant="body2">Combinations:</Typography>
                    {combineWith.length > 0 ? (
                      <Stack direction="row" spacing={0.5}>
                        {combineWith.map((item) => (
                          <MDBadge
                            key={item}
                            badgeContent={item}
                            color="success"
                            variant="gradient"
                            size="sm"
                          />
                        ))}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="tomato">
                        None
                      </Typography>
                    )}
                  </Stack>

                  {tags.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Typography variant="body2">Tags:</Typography>
                      {tags.map((item) => (
                        <MDBadge
                          key={item}
                          badgeContent={item}
                          color="success"
                          variant="gradient"
                          size="sm"
                        />
                      ))}
                    </Stack>
                  )}

                  {combineWithTags.length > 0 && (
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Typography variant="body2">Combine with tags:</Typography>
                      {combineWithTags.map((item) => (
                        <MDBadge
                          key={item}
                          badgeContent={item}
                          color="success"
                          variant="gradient"
                          size="sm"
                        />
                      ))}
                    </Stack>
                  )}
                </Stack>
              </Stack>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
}

ModalDiscount.defaultProps = {
  active: true,
  onClose: () => {},
  dataIndex: 0,
};

ModalDiscount.propTypes = {
  active: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dataIndex: PropTypes.number,
};

export default ModalDiscount;
